import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faBullseye,
  faCalendarCheck,
  faChampagneGlasses,
  faFileSignature,
  faHandshake,
  faStar,
  faUserPlus,
  faUtensils,
} from "@fortawesome/free-solid-svg-icons";
import { Delta } from "./Kpi";
import { Card, Pill, Skeleton, TeamDot } from "./Ui";
import SourceTip, { TitleWithSource } from "./SourceTip";
import Popover from "./Popover";
import { CLIENT_SEGMENTS, clientSegment, clientsSub, formatDate, formatDecimal, formatNumber, moneyOrDash, teamShortName } from "../metrics";

// Overview "Work Plan Points" section: headline numbers for the selected
// period grouped into booked / held / closed, each with the change vs the
// previous period (the API's `comparison` block: for This week / Last week
// that is the whole previous Sun-Sat week) and bars for the last 8 full weeks
// (from the API's `weekly` block).

const BOOKED_METRICS = [
  {
    key: "bluesBooked",
    label: "Client meetings",
    icon: faHandshake,
    color: "#0ea5e9",
    sub: (k) => `${formatNumber(k.clientMeetings)} client · ${formatNumber(k.ppClientMeetings)} PP`,
  },
  { key: "tapsBooked", label: "TAPs", icon: faBullseye, color: "#2563eb", sub: (k) => `${formatNumber(k.tapsHeld)} TAPs held` },
  { key: "lmv", label: "LMVs", icon: faBuilding, color: "#f59e0b", sub: (k) => `${formatNumber(k.lmvHeld)} LMVs held` },
];

const HELD_METRICS = [
  {
    key: "meetingsHappened",
    label: "Meetings held",
    icon: faCalendarCheck,
    color: "#0d9488",
    sub: (k) => `${formatNumber(k.tapsHeld)} TAPs · ${formatNumber(k.callsHeld)} calls`,
  },
  { key: "eventsHeld", label: "Events held", icon: faChampagneGlasses, color: "#8b5cf6", sub: () => "BD events, up to today" },
];

// Closed: the three closed-won reports shown separately (BD deals, new
// clients signed, Curate dinners signed).
const CLOSED_METRICS = [
  {
    key: "bdDealsClosed",
    label: "Deals closed",
    icon: faFileSignature,
    color: "#059669",
    sub: (k) => (k.bdDealsAmount ? `${moneyOrDash(k.bdDealsAmount)} · All BD Closed Deals` : "All BD Closed Deals"),
  },
  { key: "newClients", label: "Signed clients", icon: faUserPlus, color: "#2563eb", sub: () => "All BD Signed Clients" },
  { key: "curateDinners", label: "Signed dinners", icon: faUtensils, color: "#db2777", sub: () => "Closed Won Curate" },
];

const weekLabel = (w) => `${formatDate(w.from, { year: false })} – ${formatDate(w.to, { year: false })}`;

// Change chip: the headline (selected period) vs the previous period from the
// API comparison. For This week / Last week the previous period is the whole
// previous Sun-Sat week, so on "This week" the % starts low on Sunday and
// climbs as the week fills in.
function ChangeChip({ comparison, dataKey, period, format = formatNumber, current }) {
  const cmp = comparison && comparison[dataKey];
  if (!cmp || !period || !period.previous) return null;
  const isWeek = period.previousKind === "week";
  const title = `${isWeek ? "This period" : "Selected period"} (${weekLabel(period)}): ${format(current)} · ${isWeek ? "previous week" : "previous period"} (${weekLabel(period.previous)}): ${format(cmp.previous)}`;
  return (
    <span className="bd-wow" title={title}>
      <Delta comparison={cmp} compact title={title} />
      <span className="bd-wow__label">
        vs {format(cmp.previous)} {isWeek ? "last wk" : "prev."}
      </span>
    </span>
  );
}

// Bars for the weekly series (oldest -> newest), latest bar highlighted.
// `zoom` scales from just below the series minimum instead of zero, for
// running counts (e.g. 39 -> 41 clients) whose changes would otherwise be flat.
export function WeekBars({ series = [], dataKey, color, height = 30, format = formatNumber, label = weekLabel, zoom = false }) {
  const values = series.map((w) => Number(w[dataKey]) || 0);
  const max = Math.max(0, ...values);
  const min = Math.min(max, ...values);
  const floor = zoom ? Math.max(0, min - Math.max(1, max - min)) : 0;
  return (
    <div className="bd-weekbars" style={{ height, "--metric-color": color }} aria-hidden="true">
      {series.map((w, i) => {
        const v = values[i];
        const pct = max > floor ? Math.max(((v - floor) / (max - floor)) * 100, v ? 8 : 0) : 0;
        return (
          <span key={w.from || w.asOf || i} className={`bd-weekbars__bar ${i === series.length - 1 ? "is-last" : ""}`} title={`${label(w)}: ${format(v)}`}>
            <i style={{ height: `${pct}%` }} />
          </span>
        );
      })}
    </div>
  );
}

function MetricRow({ def, kpis, comparison, period, weeks }) {
  return (
    <div className="bd-wp-metric" style={{ "--metric-color": def.color }}>
      <span className="bd-wp-metric__icon">
        <FontAwesomeIcon icon={def.icon} />
      </span>
      <div className="bd-wp-metric__body">
        <div className="bd-wp-metric__label">
          {def.label}
          <SourceTip metric={def.key} title={def.label} />
        </div>
        <div className="bd-wp-metric__value-row">
          <span className="bd-wp-metric__value">{formatNumber(kpis[def.key])}</span>
          <ChangeChip comparison={comparison} dataKey={def.key} period={period} current={kpis[def.key]} />
        </div>
        <div className="bd-wp-metric__sub">{def.sub(kpis)}</div>
      </div>
      <WeekBars series={weeks} dataKey={def.key} color={def.color} />
    </div>
  );
}

// Stacked bar of booked meetings by type (Blues + TAPs + LMVs = meetings booked).
function BookedShare({ kpis }) {
  const total = BOOKED_METRICS.reduce((s, d) => s + (Number(kpis[d.key]) || 0), 0);
  return (
    <div className="bd-wp-share" title={BOOKED_METRICS.map((d) => `${d.label}: ${formatNumber(kpis[d.key])}`).join(" · ")}>
      {total > 0 ? (
        BOOKED_METRICS.map((d) => <span key={d.key} style={{ width: `${((Number(kpis[d.key]) || 0) / total) * 100}%`, background: d.color }} />)
      ) : (
        <span className="bd-wp-share__empty" />
      )}
    </div>
  );
}

function GroupSkeleton({ rows }) {
  return Array.from({ length: rows }).map((_, i) => <Skeleton key={i} height={54} radius={10} />);
}

export function WorkPlanCard({ kpis, comparison, weekly, period, loading }) {
  const weeks = (weekly && weekly.weeks) || [];
  const ready = !loading && kpis;
  const rowProps = { kpis, comparison, period, weeks };
  const isWeek = !!(period && period.previousKind === "week");
  return (
    <section className="bd-wp">
      <div className="bd-wp__head">
        <div className="bd-wp__headline">
          <span className="bd-wp__icon">
            <FontAwesomeIcon icon={faStar} />
          </span>
          <div>
            <div className="bd-wp__eyebrow">
              Work Plan Points
              <SourceTip metric="pointValue" title="Workplan points" />
            </div>
            {ready ? (
              <div className="bd-wp__big-row">
                <span className="bd-wp__big">{formatDecimal(kpis.pointValue)}</span>
                <span className="bd-wp__unit">pts</span>
                <ChangeChip comparison={comparison} dataKey="pointValue" period={period} current={kpis.pointValue} format={formatDecimal} />
              </div>
            ) : (
              <Skeleton height={34} width={180} />
            )}
            <div className="bd-wp__meta">
              {ready ? `${formatNumber(kpis.points)} points · ${formatNumber(kpis.meetingsBooked)} meetings booked` : "\u00a0"}
              {period && ` · ${formatDate(period.from, { year: false })} – ${formatDate(period.to, { year: false })}`}
            </div>
          </div>
        </div>
        <div className="bd-wp__head-bars">
          <WeekBars series={weeks} dataKey="pointValue" color="var(--bd-accent)" height={46} format={formatDecimal} />
          <span className="bd-wp__bars-caption">Point value, last 8 weeks</span>
        </div>
      </div>

      <div className="bd-wp__groups">
        <div className="bd-wp-group bd-wp-group--booked">
          <div className="bd-wp-group__title">
            <span>Booked</span>
            {ready && <span className="bd-wp-group__total">{formatNumber(kpis.meetingsBooked)} meetings</span>}
          </div>
          {ready ? <BookedShare kpis={kpis} /> : <Skeleton height={8} radius={4} />}
          {ready
            ? BOOKED_METRICS.map((d) => <MetricRow key={d.key} def={d} {...rowProps} />)
            : GroupSkeleton({ rows: BOOKED_METRICS.length })}
        </div>
        <div className="bd-wp-group bd-wp-group--held">
          <div className="bd-wp-group__title">
            <span>Held</span>
          </div>
          {ready
            ? HELD_METRICS.map((d) => <MetricRow key={d.key} def={d} {...rowProps} />)
            : GroupSkeleton({ rows: HELD_METRICS.length })}
        </div>
        <div className="bd-wp-group bd-wp-group--closed">
          <div className="bd-wp-group__title">
            <span>Closed</span>
            {ready && <span className="bd-wp-group__total">{formatNumber(kpis.dealsClosedTotal)} deals + clients</span>}
          </div>
          {ready
            ? CLOSED_METRICS.map((d) => <MetricRow key={d.key} def={d} {...rowProps} />)
            : GroupSkeleton({ rows: CLOSED_METRICS.length })}
        </div>
      </div>
      <p className="bd-wp__legend">
        Big numbers = selected period · % ={" "}
        {isWeek ? "change vs the whole previous Sun–Sat week (on This week the number builds up from Sunday)" : "change vs the previous period of equal length"} · bars =
        last 8 full weeks, latest highlighted
      </p>
    </section>
  );
}

// ------------------------------------------------------------------ clients

const ALL_SEGMENT = { id: "all", label: "All", metric: "liveClients", color: "var(--bd-accent)", note: "Retainer + PPL + Commission only" };
const SEGMENT_FILTERS = [ALL_SEGMENT, ...CLIENT_SEGMENTS];

function clientDelta(series, id) {
  if (!series || series.length < 2) return null;
  const cur = series[series.length - 1][id] || 0;
  const prev = series[series.length - 2][id] || 0;
  return { cur, prev, delta: cur - prev };
}

// Fee / terms column for a client row.
function clientTerms(c) {
  if (c.retainerFee) return `${moneyOrDash(c.retainerFee)}/mo`;
  if (c.pricePerMeeting) return `${moneyOrDash(c.pricePerMeeting)}/mtg`;
  if (c.commissionRate) {
    const rate = Number(c.commissionRate);
    return `${formatDecimal(rate > 0 && rate <= 1 ? rate * 100 : rate)}% comm.`;
  }
  return c.contractType || "—";
}

function ChangeList({ title, rows, dateKey, tone }) {
  return (
    <div className="bd-client-changes__group">
      <div className={`bd-client-changes__title bd-client-changes__title--${tone}`}>
        {title} <span>{rows.length}</span>
      </div>
      {rows.length ? (
        <ul className="bd-client-changes__list">
          {rows.map((c) => (
            <li key={c.sfId}>
              <TeamDot team={c.team} />
              <span className="bd-client-changes__main">
                <a href={c.url} target="_blank" rel="noopener noreferrer" className="bd-link bd-ellipsis" title={`Open ${c.name} in Salesforce`}>
                  {c.name}
                </a>
                <small className="bd-ellipsis">
                  {[teamShortName(c.team), c.contractType, c.status && c.status !== "Active" ? c.status : null].filter(Boolean).join(" · ")}
                </small>
              </span>
              <span className="bd-client-changes__date">{formatDate(c[dateKey], { year: false })}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="bd-muted bd-client-changes__empty">None</p>
      )}
    </div>
  );
}

// "+N added · −M terminated" chip for the total, for the selected period.
// Hover shows which clients were added / terminated in the period; click pins
// the list so the Salesforce links can be used.
function ClientChange({ changes }) {
  if (!changes) return null;
  const added = changes.added || [];
  const terminated = changes.terminated || [];
  const net = added.length - terminated.length;
  const tone = net > 0 ? "up" : net < 0 ? "down" : "flat";
  const periodText = `${formatDate(changes.from, { year: false })} – ${formatDate(changes.to, { year: false })}`;
  const hoverTitle = [
    `${periodText}: +${added.length} added · −${terminated.length} terminated`,
    added.length ? `Added: ${added.map((c) => c.name).join(", ")}` : null,
    terminated.length ? `Terminated: ${terminated.map((c) => c.name).join(", ")}` : null,
  ]
    .filter(Boolean)
    .join("\n");
  return (
    <Popover
      label="Clients added and terminated in the selected period"
      width={340}
      popClassName="bd-client-changes"
      trigger={(props, { open }) => (
        <button type="button" className={`bd-delta bd-delta--${tone} bd-delta--compact bd-delta--btn bd-client-change ${open ? "is-open" : ""}`} title={open ? undefined : hoverTitle} {...props}>
          <span className="bd-client-change__up">+{formatNumber(added.length)} added</span>
          <span className="bd-client-change__sep">·</span>
          <span className="bd-client-change__down">−{formatNumber(terminated.length)} terminated</span>
        </button>
      )}
    >
      {({ pinned }) => (
        <>
          <div className="bd-source-tip__title">
            <span>Clients · selected period</span>
            <span className="bd-source-tip__kicker">{periodText}</span>
          </div>
          <p className="bd-source-tip__how">
            {formatNumber(added.length)} added and {formatNumber(terminated.length)} terminated in the period (net {net > 0 ? "+" : net < 0 ? "−" : ""}
            {formatNumber(Math.abs(net))}). A client signed and terminated inside the period appears in both lists.
          </p>
          <ChangeList title="Added" rows={added} dateKey="sfCreatedDate" tone="up" />
          <ChangeList title="Terminated" rows={terminated} dateKey="terminatedDate" tone="down" />
          <div className="bd-source-tip__foot">
            <span>Created Date / Terminated Date on the BD Client record</span>
            <span>{pinned ? "Click to close" : "Click to pin"}</span>
          </div>
        </>
      )}
    </Popover>
  );
}

// Stacked share bar of live clients by segment.
function SegmentShare({ kpis }) {
  const total = CLIENT_SEGMENTS.reduce((s, d) => s + (Number(kpis[d.metric]) || 0), 0);
  return (
    <div className="bd-wp-share" title={CLIENT_SEGMENTS.map((d) => `${d.label}: ${formatNumber(kpis[d.metric])}`).join(" · ")}>
      {total > 0 ? (
        CLIENT_SEGMENTS.map((d) => <span key={d.id} style={{ width: `${((Number(kpis[d.metric]) || 0) / total) * 100}%`, background: d.color }} />)
      ) : (
        <span className="bd-wp-share__empty" />
      )}
    </div>
  );
}

export function ClientsCard({ kpis, lists, weekly, period, loading, changes }) {
  const [segment, setSegment] = useState("all");
  const series = (weekly && weekly.clients) || [];
  const seg = SEGMENT_FILTERS.find((s) => s.id === segment) || ALL_SEGMENT;
  const rows = useMemo(() => {
    if (!lists) return [];
    if (segment === "all") return CLIENT_SEGMENTS.flatMap((s) => lists[s.id] || []);
    return lists[segment] || [];
  }, [lists, segment]);
  const periodFrom = period ? new Date(`${period.from}T00:00:00Z`) : null;
  const ready = !loading && kpis;
  return (
    <Card
      className="bd-clients-card"
      title={
        <TitleWithSource metric="liveClients" title="Clients">
          Clients
        </TitleWithSource>
      }
      subtitle={period ? `Live BD clients by contract type · ${clientsSub(period)}` : "Live BD clients by contract type"}
      flush
    >
      <div className="bd-clients-total">
        <div className="bd-clients-total__main">
          <span className="bd-clients-total__label">Total clients</span>
          {ready ? (
            <div className="bd-clients-total__value-row">
              <span className="bd-clients-total__value">{formatNumber(kpis.liveClients)}</span>
              <ClientChange changes={changes} />
            </div>
          ) : (
            <Skeleton height={34} width={120} />
          )}
          {ready ? <SegmentShare kpis={kpis} /> : <Skeleton height={8} radius={4} />}
        </div>
        <div className="bd-clients-total__trend">
          <WeekBars
            series={series}
            dataKey={segment === "all" ? "total" : segment}
            color={seg.color}
            height={40}
            zoom
            label={(w) => `As of ${formatDate(w.asOf, { year: false })}`}
          />
          <span className="bd-wp__bars-caption">{segment === "all" ? "Total" : seg.label} running count, weekly</span>
        </div>
      </div>
      <div className="bd-clients-card__segments" role="tablist" aria-label="Client segments">
        {SEGMENT_FILTERS.map((s) => {
          const delta = clientDelta(series, s.id === "all" ? "total" : s.id);
          // a div (not a button) because the SourceTip inside is itself a button
          return (
            <div
              key={s.id}
              role="tab"
              tabIndex={0}
              aria-selected={segment === s.id}
              className={`bd-clients-seg ${segment === s.id ? "is-active" : ""}`}
              style={{ "--metric-color": s.color }}
              onClick={() => setSegment(s.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSegment(s.id);
                }
              }}
              title={`${s.note}${delta && delta.delta ? ` · ${delta.delta > 0 ? "+" : "−"}${Math.abs(delta.delta)} vs 7 days ago` : ""}`}
            >
              <span className="bd-clients-seg__dot" />
              <span className="bd-clients-seg__label">
                {s.label}
                {s.id !== "all" && <SourceTip metric={s.metric} title={`${s.label} clients`} />}
              </span>
              {ready ? <span className="bd-clients-seg__value">{formatNumber(kpis[s.metric])}</span> : <Skeleton height={18} width={24} />}
            </div>
          );
        })}
      </div>
      <ul className="bd-clients-list">
        {loading && !lists
          ? Array.from({ length: 6 }).map((_, i) => (
              <li key={i}>
                <Skeleton height={16} width="70%" />
              </li>
            ))
          : rows.map((c) => {
              const isNew = periodFrom && c.sfCreatedDate && new Date(c.sfCreatedDate) >= periodFrom;
              const rowSeg = clientSegment(c.segment);
              return (
                <li key={c.sfId} className="bd-clients-list__row">
                  <TeamDot team={c.team} />
                  <span className="bd-clients-list__main">
                    <a href={c.url} target="_blank" rel="noopener noreferrer" className="bd-link bd-ellipsis" title={c.name}>
                      {c.name}
                    </a>
                    <small className="bd-ellipsis">{[teamShortName(c.team), c.accountManager && c.accountManager.name].filter(Boolean).join(" · ")}</small>
                  </span>
                  {isNew && (
                    <Pill tone="success" title="Client record created in the selected period">
                      new
                    </Pill>
                  )}
                  {segment === "all" && rowSeg && (
                    <span className="bd-clients-list__seg" style={{ "--metric-color": rowSeg.color }} title={c.contractType || rowSeg.label}>
                      {rowSeg.label}
                    </span>
                  )}
                  <span className="bd-clients-list__fee" title={c.contractType || undefined}>
                    {clientTerms(c)}
                  </span>
                </li>
              );
            })}
        {!loading && lists && !rows.length && <li className="bd-muted bd-clients-list__empty">No {segment === "all" ? "live" : seg.label.toLowerCase()} clients</li>}
      </ul>
    </Card>
  );
}

